export interface RoleTab {
  id: string;
  label: string;
  icon: string;
  badge: string;
  badgeClass: string;
}

export interface RoleMeta {
  roleLabel: string;
  initials: string;
  name: string;
  roleClass: string;
}

export interface SummaryItem {
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}

export interface LogRow {
  id: string;
  dbId?: string;
  description: string;
  sub: string;
  submitted: string;
  amount: string;
  amountTone: string;
  daysImpact: string;
  status: string;
  statusClass: string;
}

export interface TimelineItem {
  id: number;
  status: 'done' | 'pending';
  title: string;
  meta: string;
}

export interface ModalConfig {
  title: string;
  confirmLabel: string;
  noteLabel: string;
  actionClass: string;
  message: string;
}

export interface CoDetails {
  project: string;
  coNumber: string;
  date: string;
  title: string;
  trade: string;
  reason: string;
  scheduleImpact: string;
  contractDateImpact: string;
  description: string;
  scopeNotes: string;
}

export interface LineItem {
  id: number;
  description: string;
  unit: string;
  qty: number;
  unitCost: number;
  amount: number;
  aiaItemId: string;
  aiaItemCode: number | string;
  subContractorId?: string;
  subContractorName?: string;
}

export const roleTabs: RoleTab[] = [
  {
    id: "gc",
    label: "GC View",
    icon: "GC",
    badge: "Submit CO",
    badgeClass: "vbNew",
  },
  {
    id: "bank",
    label: "Bank / Lender View",
    icon: "BK",
    badge: "2 Pending",
    badgeClass: "vbPending",
  },
  {
    id: "admin",
    label: "CSC Admin View",
    icon: "AD",
    badge: "Full Log",
    badgeClass: "vbApproved",
  },
];

export const roleMeta: Record<string, RoleMeta> = {
  gc: {
    roleLabel: "General Contractor",
    initials: "JD",
    name: "J. DeStefano",
    roleClass: "navPillGc",
  },
  bank: {
    roleLabel: "Lender / Bank",
    initials: "KY",
    name: "K. Youland",
    roleClass: "navPillLender",
  },
  admin: {
    roleLabel: "CSC Admin",
    initials: "JG",
    name: "J. Gregor",
    roleClass: "navPillAdmin",
  },
};

export const gcSummary: SummaryItem[] = [
  { label: "Original Contract", value: "$1,429,690" },
  { label: "Approved COs", value: "+$24,150", sub: "2 approved", tone: "up" },
  {
    label: "Pending COs",
    value: "+$18,400",
    sub: "1 pending bank",
    tone: "blue",
  },
  {
    label: "Revised Contract",
    value: "$1,453,840",
    sub: "Incl. approved only",
  },
  { label: "Total CO #", value: "3", sub: "Submitted to date" },
];

export const gcLogRows: LogRow[] = [
  {
    id: "CO-003",
    description: "Ledge encountered - additional blasting required",
    sub: "Sitework - Owner-directed",
    submitted: "Mar 18, 2026",
    amount: "+$18,400",
    amountTone: "pos",
    daysImpact: "+3 days",
    status: "Pending Bank",
    statusClass: "bPending",
  },
  {
    id: "CO-002",
    description: "Electrical panel upgrade - 200A to 400A service",
    sub: "Electrical - GC-initiated",
    submitted: "Feb 14, 2026",
    amount: "+$12,800",
    amountTone: "pos",
    daysImpact: "+0 days",
    status: "Approved",
    statusClass: "bApproved",
  },
  {
    id: "CO-001",
    description: "Owner-directed: add emergency exit signage package",
    sub: "Life Safety - Owner-directed",
    submitted: "Jan 22, 2026",
    amount: "+$11,350",
    amountTone: "pos",
    daysImpact: "+0 days",
    status: "Approved",
    statusClass: "bApproved",
  },
];

export const gcSteps: string[] = [
  "CO Details",
  "Line Items & Scope",
  "Supporting Documents",
  "Review & Submit",
];

export const gcNextLabels: string[] = [
  "Next: Line Items",
  "Next: Supporting Documents",
  "Next: Review & Submit",
];

export const tradeOptions: string[] = [
  "Select trade...",
  "Sitework / Earthwork",
  "Concrete",
  "Framing / Carpentry",
  "Roofing",
  "Electrical",
  "Plumbing",
  "HVAC / Mechanical",
  "Drywall / Finishes",
  "Life Safety",
  "General Conditions",
  "Other",
];

export const reasonOptions: string[] = [
  "Select reason...",
  "Owner-directed change",
  "Unforeseen site condition",
  "Design change by architect",
  "Code / municipality requirement",
  "GC-initiated value engineering",
  "Material substitution",
  "Weather delay",
  "Other",
];

export const initialCoDetails: CoDetails = {
  project: "Aroma Joes - Portland",
  coNumber: "CO-004",
  date: "2026-03-26",
  title: "",
  trade: "Sitework / Earthwork",
  reason: "Owner-directed change",
  scheduleImpact: "+2 days",
  contractDateImpact: "",
  description: "",
  scopeNotes: "",
};

export const initialLineItems: LineItem[] = [
  { id: 1, description: "", unit: "LS", qty: 1, unitCost: 0, amount: 0, aiaItemId: "", aiaItemCode: "" },
  { id: 2, description: "", unit: "LS", qty: 1, unitCost: 0, amount: 0, aiaItemId: "", aiaItemCode: "" },
];

export interface SOVVersion {
  id: string;
  versionNumber?: number;
  status: string;
  createdAt?: string;
}

export interface SovLienItem {
  id?: string;
  aiaItemId: string;
  aiaItemCode: string | number;
  description: string;
  amount?: { amount: number; currency?: { symbol: string } };
  subContractorId?: string;
  subContractor?: { companyName?: string };
  status?: string;
  isDeleted?: boolean;
}

export interface SOVVersionDetails {
  id: string;
  versionNumber?: number;
  status: string;
  sovLienItems?: SovLienItem[];
}

export const requiredChecklist: string[] = [
  "Subcontractor quote(s) attached if applicable",
  "Photos of site condition attached if applicable",
  "Change order description is complete and accurate",
  "Cost breakdown reflects actual or estimated cost to owner",
  "Schedule impact noted (or confirmed as zero)",
];

export const bankSummary: SummaryItem[] = [
  { label: "Original Contract", value: "$1,429,690" },
  { label: "Approved COs", value: "+$24,150", sub: "2 approved", tone: "up" },
  {
    label: "Pending Approval",
    value: "+$27,000",
    sub: "2 pending",
    tone: "blue",
  },
  { label: "Revised Contract", value: "$1,453,840", sub: "Approved only" },
  {
    label: "If All Approved",
    value: "$1,480,840",
    sub: "+$51,150 total",
    tone: "up",
  },
];

export const bankTimeline: TimelineItem[] = [
  {
    id: 1,
    status: "done",
    title: "Submitted by GC",
    meta: "DeStefano & Associates - March 18, 2026 - 9:44 AM",
  },
  {
    id: 2,
    status: "done",
    title: "CSC New England notified",
    meta: "J. Gregor notified - March 18, 2026",
  },
  {
    id: 3,
    status: "pending",
    title: "Awaiting Bank Approval",
    meta: "Androscoggin Bank - K. Youland - 8 days pending",
  },
];

export const approvedLogRows: LogRow[] = [
  {
    id: "CO-002",
    description: "Electrical panel upgrade - 200A to 400A service",
    sub: "Approved Feb 18, 2026",
    submitted: "Feb 14, 2026",
    amount: "+$12,800",
    amountTone: "pos",
    daysImpact: "None",
    status: "Approved",
    statusClass: "bApproved",
  },
  {
    id: "CO-001",
    description: "Owner-directed: add emergency exit signage package",
    sub: "Approved Jan 26, 2026",
    submitted: "Jan 22, 2026",
    amount: "+$11,350",
    amountTone: "pos",
    daysImpact: "None",
    status: "Approved",
    statusClass: "bApproved",
  },
];

export const adminSummary: SummaryItem[] = [
  { label: "Total COs - All Projects", value: "17", sub: "Year to date" },
  {
    label: "Total CO Value Approved",
    value: "+$148,620",
    sub: "Across 6 projects",
    tone: "up",
  },
  { label: "Pending Approval", value: "4", sub: "At 3 banks", tone: "blue" },
  { label: "Rejected", value: "2", sub: "Returned to GC", tone: "red" },
  { label: "Avg Approval Time", value: "4.8 days", sub: "Bank turnaround" },
];

export const adminLogRows: LogRow[] = [
  {
    id: "CO-004",
    description: "Gas line relocation - Aroma Joes Portland",
    sub: "DeStefano - TD Bank",
    submitted: "Mar 24, 2026",
    amount: "+$8,600",
    amountTone: "pos",
    daysImpact: "None",
    status: "Pending Bank",
    statusClass: "bPending",
  },
  {
    id: "CO-003",
    description: "Ledge - additional blasting - Aroma Joes Portland",
    sub: "DeStefano - Androscoggin Bank",
    submitted: "Mar 18, 2026",
    amount: "+$18,400",
    amountTone: "pos",
    daysImpact: "+3 days",
    status: "Pending Bank",
    statusClass: "bPending",
  },
  {
    id: "CO-002",
    description: "Electrical panel upgrade - Aroma Joes Portland",
    sub: "DeStefano - Androscoggin Bank",
    submitted: "Feb 14, 2026",
    amount: "+$12,800",
    amountTone: "pos",
    daysImpact: "None",
    status: "Approved",
    statusClass: "bApproved",
  },
  {
    id: "CO-F04-01",
    description: "Window brand upgrade - Owner-directed - Flewelling",
    sub: "BTB Build - Kennebunk Savings",
    submitted: "Feb 28, 2026",
    amount: "+$4,200",
    amountTone: "pos",
    daysImpact: "None",
    status: "Rejected",
    statusClass: "bRejected",
  },
];

export const modalConfig: Record<string, ModalConfig> = {
  approve: {
    title: "Approve Change Order",
    confirmLabel: "Confirm Approval",
    noteLabel: "Notes (optional)",
    actionClass: "btnApprove",
    message:
      "Change order approved. Contract value updated. GC and CSC notified.",
  },
  reject: {
    title: "Reject Change Order",
    confirmLabel: "Confirm Rejection",
    noteLabel: "Reason for Rejection (required)",
    actionClass: "btnReject",
    message: "Change order rejected. GC has been notified.",
  },
  inspect: {
    title: "Request More Information",
    confirmLabel: "Send Request",
    noteLabel: "Notes (optional)",
    actionClass: "btnPrimary",
    message: "Request sent to GC for additional documentation.",
  },
};
